import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { Document, Plus, Template } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { getMenuItemClassName, getMenuItemContentClassNames } from '@/components/menu/menu-item-styles'
import TruncatedText from '@/components/truncated-text/TruncatedText'

export type TurnAttachedFileChip = {
  key: string
  name: string
  isWritingSample: boolean
  disabled: boolean
  onOpen?: () => void
}

type TurnAttachedFilesDropdownTriggerProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'children'> & { count: number }

/** The "+N more" button that opens the dropdown. */
export const TurnAttachedFilesDropdownTrigger = forwardRef<HTMLButtonElement, TurnAttachedFilesDropdownTriggerProps>(function TurnAttachedFilesDropdownTrigger(
  { count, ...props },
  ref
) {
  return (
    <Button
      ref={ref}
      variant="plain"
      color="neutral"
      size="xs"
      leadingIcon={<Plus className="size-3.5" />}
      className="shrink-0 data-[state=open]:bg-brand-neutral-100"
      aria-label={`${count} more ${count === 1 ? 'file' : 'files'}`}
      {...props}
    >
      {count} more
    </Button>
  )
})

/** Lists the turn's files that don't fit in the chip rows, each opening its file. */
export function TurnAttachedFilesDropdown({ chips }: { chips: TurnAttachedFileChip[] }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <TurnAttachedFilesDropdownTrigger count={chips.length} />
      </DropdownMenu.Trigger>
      {/* Keeps a gap from the viewport edge, with the list scrolling once it's taller than the space left */}
      <MenuContent align="end" collisionPadding={16} className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-[260px] overflow-y-auto">
        <MenuItemGroup>
          {chips.map((chip) => {
            const disabled = chip.disabled || !chip.onOpen
            const { iconClassName, labelClassName } = getMenuItemContentClassNames({ disabled, variant: 'neutral' })
            const Icon = chip.isWritingSample ? Template : Document
            return (
              <DropdownMenu.Item key={chip.key} disabled={disabled} onSelect={chip.onOpen} className={getMenuItemClassName({ disabled, variant: 'neutral' })}>
                <Icon className={iconClassName} aria-hidden />
                <TruncatedText className={labelClassName} disableOnTouch>
                  {chip.name}
                </TruncatedText>
              </DropdownMenu.Item>
            )
          })}
        </MenuItemGroup>
      </MenuContent>
    </DropdownMenu.Root>
  )
}
